import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  CircleCheck,
  ExternalLink,
  FileText,
  Hourglass,
  Landmark,
  Loader2,
  Lock,
  MapPin,
  Minus,
  PartyPopper,
  Send,
  ShieldCheck,
  Sparkles,
  Upload,
  UserCheck,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { stepKindOf as kindOf } from './journey';
import PoaCard, { poaCardShows } from './PoaCard';
import { PRIMARY, SECONDARY, Steps, TaskCard, YourPart } from './TaskCard';
import { useSayVaGovDone } from '../../../../services/onboarding';
import { useFileUpload } from '../../../../hooks/useFileUpload';

// "Right now" (Nic 2026-10-10): the one thing that matters this minute, so a
// client always knows what's happening and what, if anything, is on them.

const CARD =
  'overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-slate-900 dark:ring-1 dark:ring-white/10';
// PRIMARY and SECONDARY (the buttons) live in TaskCard.js (Nic 2026-10-27),
// shared with the task cards.

// The top band: an icon, a small eyebrow, the headline and a sentence.
const Hero = ({ tone, icon: Icon, eyebrow, title, children }) => (
  <div
    className={cn(
      'flex gap-4 px-[20px] pb-5 pt-[22px] sm:px-[26px]',
      tone === 'amber' && 'bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/10 dark:to-transparent',
      tone === 'sky' && 'bg-gradient-to-br from-sky-50 to-white dark:from-sky-500/10 dark:to-transparent',
      tone === 'emerald' && 'bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-500/10 dark:to-transparent',
      tone === 'slate' && 'bg-gradient-to-br from-slate-50 to-white dark:from-white/5 dark:to-transparent',
    )}
  >
    <span
      className={cn(
        'flex size-11 shrink-0 items-center justify-center rounded-2xl',
        tone === 'amber' && 'bg-amber-500 text-white',
        tone === 'sky' && 'bg-sky-600 text-white',
        tone === 'emerald' && 'bg-emerald-500 text-white',
        tone === 'slate' && 'bg-slate-500 text-white',
      )}
    >
      <Icon size={22} />
    </span>
    <div className="min-w-0 flex-1">
      <div
        className={cn(
          'text-[11px] font-bold uppercase tracking-wider',
          tone === 'amber' && 'text-amber-700 dark:text-amber-300',
          tone === 'sky' && 'text-sky-700 dark:text-sky-300',
          tone === 'emerald' && 'text-emerald-700 dark:text-emerald-300',
          tone === 'slate' && 'text-slate-500 dark:text-slate-400',
        )}
      >
        {eyebrow}
      </div>
      <h3 className="mb-0 mt-0.5 text-[19px] font-bold leading-snug text-slate-900 dark:text-white sm:text-xl">
        {title}
      </h3>
      <div className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{children}</div>
    </div>
  </div>
);

// The checklist in three clear groups (Nic 2026-10-23, "super clear for our
// clients"): what they still need to send, what they sent that their
// specialist is reviewing, and what their specialist checked off. Each group
// has its own look: a plain document (to send), a blue hourglass (with their
// specialist), a green check (confirmed).
const KIND = {
  todo: {
    tile: 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300',
  },
  review: {
    tile: 'bg-sky-50 text-sky-600 ring-1 ring-sky-100 dark:!bg-sky-500/15 dark:text-sky-300 dark:ring-sky-400/20',
  },
  requested: {
    tile: 'bg-violet-50 text-violet-600 ring-1 ring-violet-100 dark:!bg-violet-500/15 dark:text-violet-300 dark:ring-violet-400/20',
  },
  done: {
    tile: 'bg-emerald-50 text-emerald-600 dark:!bg-emerald-500/15 dark:text-emerald-300',
  },
  na: {
    tile: 'bg-slate-100 text-slate-400 dark:!bg-white/10 dark:text-slate-500',
  },
};

// Upload (solid) for a step still to send; Add more (lighter, the same hover)
// once they sent something.
const UPLOAD_BTN =
  'inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3.5 text-sm font-semibold outline-none transition-[background-color,color] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:bg-slate-200 disabled:text-slate-400 dark:disabled:!bg-white/10 dark:disabled:!text-slate-500';
const UPLOAD_SOLID = 'bg-slate-900 text-white hover:bg-slate-700 dark:!bg-white dark:!text-slate-900 dark:hover:!bg-slate-200';
const UPLOAD_SOFT =
  'bg-slate-200 text-slate-800 hover:bg-slate-700 hover:text-white dark:!bg-white/15 dark:!text-slate-100 dark:hover:!bg-slate-200 dark:hover:!text-slate-900';

// One step, in its group's look, with its own upload.
const NeedRow = ({ item, locked, uploading, onUpload, specialist }) => {
  const input = useRef(null);
  const kind = kindOf(item);
  const isAddress = item.key === 'address';
  const Icon =
    kind === 'done'
      ? Check
      : kind === 'na'
        ? Minus
        : kind === 'review'
          ? Hourglass
          : kind === 'requested'
            ? Send
            : isAddress
              ? MapPin
              : FileText;
  const files = `${item.uploads} file${item.uploads === 1 ? '' : 's'}`;
  const who = specialist || 'Your specialist';
  const sub =
    kind === 'done'
      ? `Checked off by ${who}`
      : kind === 'na'
        ? 'Not needed for your claim'
      : kind === 'requested'
        ? // It never holds up their claim (it can follow them to their Claims
          // Manager), so they know which one isn't the holdup (Nic 2026-10-26).
          "This was requested from the VA. Once you receive the email, you can upload it here. It won't hold up your claim."
        : kind === 'review'
          ? `${files} sent · ${who} will check ${item.uploads === 1 ? 'it' : 'them'} and tick this off`
        : isAddress
          ? `${who} confirms it with you on a call`
          : null;
  return (
    <li className="flex items-center gap-3 px-[20px] py-3 sm:px-[26px]">
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', KIND[kind].tile)}>
        <Icon size={17} strokeWidth={kind === 'done' ? 2.75 : 2} />
      </span>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'text-sm font-semibold',
            kind === 'done' || kind === 'na' ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-slate-100',
          )}
        >
          {item.label}
        </div>
        {sub ? (
          <div
            className={cn(
              'text-xs',
              kind === 'review'
                ? 'font-medium text-sky-700 dark:text-sky-300'
                : kind === 'requested'
                  ? 'font-medium text-violet-700 dark:text-violet-300'
                  : 'text-slate-500 dark:text-slate-400',
            )}
          >
            {sub}
          </div>
        ) : null}
      </div>
      {!item.auto && kind !== 'done' && kind !== 'na' ? (
        <>
          <input
            ref={input}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) onUpload(item.key, e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={locked || uploading}
            onClick={() => input.current?.click()}
            aria-label={`${kind === 'review' ? 'Add more to' : 'Upload'} ${item.label}`}
            // A requested record counts as one to send (Nic 2026-10-26), so
            // its Upload looks like the rest; only Add more is the lighter one.
            className={cn(UPLOAD_BTN, kind === 'todo' || kind === 'requested' ? UPLOAD_SOLID : UPLOAD_SOFT)}
          >
            {uploading ? <Loader2 size={15} className="animate-spin" /> : locked ? <Lock size={15} /> : <Upload size={15} />}
            <span className="hidden sm:inline">{kind === 'review' ? 'Add more' : 'Upload'}</span>
          </button>
        </>
      ) : null}
    </li>
  );
};

// A group's header: a small label, a count, and a line on what it means.
const GroupHead = ({ label, count, tone, note }) => (
  <div className="flex items-baseline gap-2 px-[20px] pb-1 pt-3.5 sm:px-[26px]">
    <span
      className={cn(
        'text-[11px] font-bold uppercase tracking-wider',
        tone === 'sky' ? 'text-sky-700 dark:text-sky-300' : 'text-slate-500 dark:text-slate-400',
      )}
    >
      {label}
    </span>
    <span
      className={cn(
        'rounded-full px-1.5 text-[11px] font-bold tabular-nums',
        tone === 'sky'
          ? 'bg-sky-100 text-sky-700 dark:!bg-sky-500/20 dark:text-sky-200'
          : 'bg-slate-100 text-slate-600 dark:!bg-white/10 dark:text-slate-300',
      )}
    >
      {count}
    </span>
    {note ? <span className="ml-auto hidden text-xs text-slate-400 sm:inline">{note}</span> : null}
  </div>
);

const Needs = ({ onboarding, locked, lockedNote, specialistName }) => {
  const queryClient = useQueryClient();
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const { startUpload } = useFileUpload(onboarding.claimId, () => {});
  const items = onboarding.items.filter((i) => i.key !== 'va_gov' && (!i.auto || i.key === 'address'));
  const toSend = items.filter((i) => kindOf(i) === 'todo');
  // Requested (the service treatment record) stays on their list, last, and
  // counts (Nic 2026-10-26): they upload it when the VA sends it.
  const requested = items.filter((i) => kindOf(i) === 'requested');
  const inReview = items.filter((i) => kindOf(i) === 'review');
  const done = items.filter((i) => ['done', 'na'].includes(kindOf(i)));
  const pct = Math.round((onboarding.progress.done / onboarding.progress.total) * 100);
  const who = specialistName || 'Your specialist';

  const upload = async (key, files) => {
    setUploadingKey(key);
    try {
      const { uploaded } = await startUpload(files, null, null, { onboardingItem: key });
      if (uploaded) toast.success(`Sent. ${who} will review it.`);
      queryClient.invalidateQueries(['my-onboarding']);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That upload didn't go through. Please try again.");
    } finally {
      setUploadingKey(null);
    }
  };
  const rows = (list) => (
    <ul className={cn('mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]', locked && 'opacity-60')}>
      {list.map((item) => (
        <NeedRow
          key={item.key}
          item={item}
          locked={locked}
          uploading={uploadingKey === item.key}
          onUpload={upload}
          specialist={specialistName}
        />
      ))}
    </ul>
  );

  return (
    <div className="border-t-[1px] border-slate-100 dark:!border-white/10">
      <div className="flex items-center gap-3 px-[20px] pb-1 pt-4 sm:px-[26px]">
        {/* "Your documents", apart from "Your part" above it (Nic 2026-10-27). */}
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Your documents</div>
        <div className="ml-auto flex items-center gap-2">
          <div className="hidden h-1.5 w-28 overflow-hidden rounded-full bg-slate-100 dark:!bg-white/10 sm:block">
            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-semibold tabular-nums text-slate-500 dark:text-slate-400">
            {onboarding.progress.done} of {onboarding.progress.total} checked off
          </span>
        </div>
      </div>
      {locked ? (
        <div className="mx-[20px] mt-2 flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500 dark:!bg-white/5 dark:text-slate-400 sm:mx-[26px]">
          <Lock size={13} className="shrink-0" /> {lockedNote || 'These open up once your VA.gov account is set up.'}
        </div>
      ) : null}

      {toSend.length || requested.length ? (
        <>
          <GroupHead label="To send" count={toSend.length + requested.length} note="Upload what you have handy" />
          {rows([...toSend, ...requested])}
        </>
      ) : (
        <div className="flex items-center gap-2 px-[20px] py-4 text-sm font-semibold text-emerald-700 dark:text-emerald-300 sm:px-[26px]">
          <CircleCheck size={17} /> Nothing left for you to send. Nice work.
        </div>
      )}

      {inReview.length ? (
        <div className="mt-1 border-t-[1px] border-sky-100 bg-sky-50/40 dark:!border-sky-400/10 dark:!bg-sky-500/[0.04]">
          <GroupHead label={`With ${who} for review`} count={inReview.length} tone="sky" note="Nothing needed from you" />
          {rows(inReview)}
        </div>
      ) : null}

      {done.length ? (
        <div className="border-t-[1px] border-slate-100 dark:!border-white/10">
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            className="flex w-full items-center gap-2 px-[20px] py-3 text-left text-xs font-semibold text-slate-500 outline-none hover:text-slate-700 focus:outline-none dark:text-slate-400 dark:hover:text-slate-200 sm:px-[26px]"
          >
            <span className="flex size-5 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:!bg-emerald-500/15 dark:text-emerald-300">
              <Check size={12} strokeWidth={3} />
            </span>
            {done.length} done
            <span className="ml-auto">{showDone ? 'Hide' : 'Show'}</span>
          </button>
          {showDone ? rows(done) : null}
        </div>
      ) : null}
    </div>
  );
};

// The rows they still owe past onboarding, each with its upload (the server's
// stillNeeded):
//   private_records   assigned to their Claims Manager before their private
//                     treatment records came in (Nic 2026-10-26)
//   service_records   a requested service treatment record, in Ready for CM
//                     and with their Claims Manager (QA 2026-10-26), until
//                     it's checked off
// `name` is who sees what they send: their Claims Manager, or their
// specialist while they wait for one.
const StillNeeded = ({ onboarding, name, fallback = 'Your Claims Manager' }) => {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const { startUpload } = useFileUpload(onboarding.claimId, () => {});
  const items = (onboarding.items || []).filter((i) => (onboarding.stillNeeded || []).includes(i.key));
  if (!items.length) return null;
  const who = name || fallback;
  const locked = onboarding.uploadsOpen === false;
  const upload = async (key, files) => {
    setUploading(key);
    try {
      const { uploaded } = await startUpload(files, null, null, { onboardingItem: key });
      if (uploaded) toast.success(`Sent. ${who} will see it.`);
      queryClient.invalidateQueries(['my-onboarding']);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That upload didn't go through. Please try again.");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="border-t-[1px] border-slate-100 dark:!border-white/10" data-testid="still-needed">
      <GroupHead label="Still needed" count={items.length} note="Upload it when you have it" />
      <ul className="mb-0 list-none divide-y divide-slate-100 pl-0 dark:divide-white/[0.06]">
        {items.map((item) => (
          <NeedRow
            key={item.key}
            // Theirs to send until a file is in; then it's with `who`. A
            // requested record keeps its "requested from the VA" line.
            item={{ ...item, status: 'todo' }}
            locked={locked}
            uploading={uploading === item.key}
            onUpload={upload}
            specialist={name}
          />
        ))}
      </ul>
    </div>
  );
};

// Set up VA.gov, as a task card in "Your part" (Nic 2026-10-27; it was the
// card's hero). Said done (their tap, no bounce after it) reads as waiting on
// their specialist; in practice their tap moves them straight on to
// documents, so that row is for the day the server keeps them here.
const VaGovStep = ({ onboarding, specialistName, n, current }) => {
  const queryClient = useQueryClient();
  const sayDone = useSayVaGovDone({ onSuccess: () => queryClient.invalidateQueries(['my-onboarding']) });
  const va = onboarding.vaGov || {};
  const who = specialistName || 'Your specialist';
  const said = !!va.saidDoneAt && !va.bouncedAt;
  return (
    <TaskCard
      n={n}
      tone="amber"
      icon={ShieldCheck}
      title="Set up your VA.gov account"
      why="Everything else waits on this. VA.gov is where we pull your records and file for you, so you need to be able to sign in."
      status={said ? 'waiting' : va.bouncedAt ? 'sentBack' : 'todo'}
      // Checked with their specialist and not set up yet (Nic 2026-10-05).
      note={va.bouncedAt ? "On your call it wasn't set up yet. Finish VA.gov's setup, make sure you can sign in, then tap I've done it." : null}
      footer="Stuck? VA.gov's support line is 866-279-3677."
      current={current}
      done={said ? `You said it's set up. ${who} will confirm on your call.` : null}
      testId="vagov-card"
    >
      <Steps
        steps={[
          // VA.gov's own account guide (Nic 2026-10-26), not the home page.
          'Open VA.gov’s guide below and follow its steps to make your account.',
          'Already have one? Make sure you can sign in on VA.gov.',
          'Once you can sign in, come back here and tap I’ve done it. We’ll set up a call.',
        ]}
      />
      <div className="flex flex-col gap-2 sm:flex-row">
        <a href={va.setupUrl || 'https://www.va.gov/resources/creating-an-account-for-vagov/'} target="_blank" rel="noreferrer" className={PRIMARY}>
          <ExternalLink size={16} /> Open VA.gov
        </a>
        {/* Their word checks it off for them (Nic 2026-10-05); their specialist
            confirms it on a call. "Next call", not "first" (Nic 2026-10-25: a
            client sent back after their first call saw "first call"). */}
        <button
          type="button"
          disabled={sayDone.isLoading}
          onClick={() =>
            sayDone.mutate(undefined, {
              onSuccess: () =>
                toast.success('Thanks! Your specialist will check it with you on your next call.'),
              onError: (e) => toast.error(e?.response?.data?.msg || "That didn't go through."),
            })
          }
          className={SECONDARY}
        >
          {sayDone.isLoading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} I&apos;ve done it
        </button>
      </div>
    </TaskCard>
  );
};

// "Your part" (Nic 2026-10-27): the tasks only they can do, as task cards at
// the top. VA.gov while it isn't set up; their old POA while their specialist
// waits on it (not an `items` row and never locked by VA.gov). The first one
// not yet said done is open; the rest open from "See the steps".
const yourPartOf = (journey, ob) => {
  if (!ob || !['vagov', 'documents', 'review'].includes(journey.now)) return [];
  const tasks = [];
  if (journey.now === 'vagov') tasks.push({ key: 'vagov', said: !!ob.vaGov?.saidDoneAt && !ob.vaGov?.bouncedAt });
  if (poaCardShows(ob.poa)) tasks.push({ key: 'poa', said: ob.poa.stage === 'client_done' });
  return tasks;
};

const NowCard = ({ journey, onboarding, cmName, specialistName, todoCount, openInvoice, onGo }) => {
  const ob = onboarding && onboarding.state ? onboarding : null;
  const tasks = yourPartOf(journey, ob);
  const currentTask = tasks.find((t) => !t.said)?.key || null;
  const yourPart = tasks.length ? (
    <YourPart toDo={tasks.filter((t) => !t.said).length}>
      {tasks.map((t, i) =>
        t.key === 'vagov' ? (
          <VaGovStep key={t.key} onboarding={ob} specialistName={specialistName} n={i + 1} current={currentTask === t.key} />
        ) : (
          <PoaCard key={t.key} poa={ob.poa} specialistName={specialistName} n={i + 1} current={currentTask === t.key} />
        ),
      )}
    </YourPart>
  ) : null;
  let body;
  switch (journey.now) {
    case 'vagov':
      // The VA.gov task card is the hero.
      body = null;
      break;
    case 'documents':
    case 'review':
      body = (
        <Hero
          tone={journey.now === 'review' ? 'emerald' : 'sky'}
          icon={journey.now === 'review' ? BadgeCheck : FileText}
          eyebrow={journey.now === 'review' ? 'All sent' : 'Your next step'}
          title={
            journey.now === 'review'
              ? "You've sent everything we asked for"
              : ob
                ? `Send us ${todoCount ? `${todoCount} more document${todoCount === 1 ? '' : 's'}` : 'your documents'}`
                : 'Send us your documents'
          }
        >
          {journey.now === 'review'
            ? `${specialistName || 'Your specialist'} is reviewing everything and will reach out if anything else is needed.`
            : ob
              ? // Optional, never a chore (Nic 2026-10-21): upload what's handy, or
                // do it together on a call (not "first": it may have happened, Nic
                // 2026-10-25). No VA.gov line: it's already done.
                // The reassurance stands out a little (bold, the card's blue).
                (
                  <>
                    If you have any of these handy, you're welcome to upload them below. A clear photo from your
                    phone works.{' '}
                    <span className="font-semibold text-slate-800 dark:text-slate-100">If not, no need to worry:</span>{' '}
                    <span className="font-semibold text-sky-700 dark:text-sky-300">
                      {specialistName || 'your Onboarding Specialist'} will go through each one with you on a call.
                    </span>
                  </>
                )
              : (
                  <>
                    If you have any documents handy, you're welcome to upload them in Documents.{' '}
                    <span className="font-semibold text-slate-800 dark:text-slate-100">If not, no need to worry:</span>{' '}
                    <span className="font-semibold text-sky-700 dark:text-sky-300">
                      we'll go through what we need with you on a call.
                    </span>
                  </>
                )}
        </Hero>
      );
      break;
    case 'matching':
      body = (
        <>
          <Hero tone="emerald" icon={UserCheck} eyebrow="Onboarding done" title="We're matching you with your Claims Manager">
            {onboarding?.carriedOver
              ? 'Your Claims Manager will reach out to introduce themselves. Nothing to do on your end.'
              : (
                  <>
                    You&apos;ve finished everything with {specialistName || 'your specialist'}. Your Claims Manager will
                    reach out to introduce themselves. Nothing to do on your end.
                  </>
                )}
          </Hero>
          {/* A requested service treatment record keeps its upload row
              (QA 2026-10-26): it arrives from the VA whenever it does. */}
          {ob?.stillNeeded?.length ? (
            <StillNeeded onboarding={ob} name={specialistName} fallback="Your specialist" />
          ) : null}
        </>
      );
      break;
    case 'building':
      body = (
        <>
          <Hero tone="sky" icon={Sparkles} eyebrow="In progress" title={`${cmName || 'Your Claims Manager'} is building your claim`}>
            They&apos;re putting your evidence together and preparing your filing. If they need anything from you,
            they&apos;ll reach out.
          </Hero>
          {ob?.stillNeeded?.length ? <StillNeeded onboarding={ob} name={cmName} /> : null}
        </>
      );
      break;
    case 'va':
      body = (
        <Hero tone="sky" icon={Landmark} eyebrow="Submitted" title="Your claim is with the VA">
          The VA is reviewing your claim. We keep an eye on it and will let you know as soon as there&apos;s a decision.
          You can also check its status on VA.gov.
        </Hero>
      );
      break;
    case 'complete':
    case 'paid':
      body = (
        <Hero
          tone="emerald"
          icon={journey.now === 'paid' ? PartyPopper : BadgeCheck}
          eyebrow={journey.now === 'paid' ? 'All done' : 'Complete'}
          title={journey.now === 'paid' ? "You're all set" : 'Your claim is complete'}
        >
          {openInvoice
            ? 'Your invoice is ready in Billing.'
            : journey.now === 'paid'
              ? 'Thank you for trusting us with your claim.'
              : 'Thank you for trusting us with your claim. Your billing details are in Billing.'}
        </Hero>
      );
      break;
    case 'closed':
    default:
      body = (
        <Hero tone="slate" icon={Hourglass} eyebrow="Closed" title="Your claim is closed">
          If you think this is a mistake or want to pick it back up, reach out to us and we&apos;ll help.
        </Hero>
      );
  }
  return (
    <section className={CARD}>
      {/* Tasks first, then the documents hero, then the documents list. */}
      {yourPart}
      {body}
      {ob && ['vagov', 'documents', 'review'].includes(journey.now) ? (
        <Needs
          onboarding={ob}
          // An inactive onboarding claim takes no uploads (QA 2026-10-26):
          // locked with a plain reason, never a failed upload.
          locked={journey.now === 'vagov' || ob.uploadsOpen === false}
          lockedNote={
            journey.now !== 'vagov' && ob.uploadsOpen === false
              ? 'Uploads are paused on your claim right now. Your specialist will reach out.'
              : null
          }
          specialistName={specialistName}
        />
      ) : null}
      {!ob && journey.now === 'documents' ? (
        <div className="px-[20px] pb-5 sm:px-[26px]">
          <button type="button" onClick={() => onGo('files')} className={PRIMARY}>
            Go to Documents <ArrowRight size={16} />
          </button>
        </div>
      ) : null}
      {openInvoice && ['complete', 'paid'].includes(journey.now) ? (
        <div className="px-[20px] pb-5 sm:px-[26px]">
          <button type="button" onClick={() => onGo('billing')} className={PRIMARY}>
            View and pay your invoice <ArrowRight size={16} />
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default NowCard;
